// App.jsx
import "./App.css";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import { RefreshProvider } from "./RefreshContext";
import API from "./api"; // ✅ instance axios (baseURL normalisée, credentials activés)
import { loadGoogleMapsPlaces } from "./utils/loadGoogleMapsScript";

import Header from "./Component/Header/header";
import NativeNotificationBridge from "./Component/NativeNotificationBridge.jsx";
import CookieConsent from "./Component/CookieConsent/CookieConsent.jsx";
import AppFooter from "./AppFooter";
import Home from "./Pages/marketing/HomePage.jsx";
import HomePro from "./Pages/pro/home_pro/homePro.jsx";
import RegisterPro from "./Pages/pro/register_pro/registerPro.jsx";
import RegisterUser from "./Pages/user/register_user/registerUser.jsx";
import Login from "./Pages/auth/LoginPage.jsx";
import WelcomePro from "./Pages/pro/welcome_pro/welcomePro.jsx";
import SearchProsPage from "./Pages/search/SearchProsPage.jsx";
import AnnonceForm from "./Component/Annonce/annonce";
import MesRdv from "./Pages/pro/mesRdv_pro/mesRdv.jsx";
import Settings from "./Pages/pro/Settings_pro/Settings.jsx";
import ActivityCard from "./Pages/user/activityCard/activityCard.jsx";
import EditAnnonce from "./Component/editAnnonce/editAnnonce.jsx";
import MesDocuments from "./Pages/pro/DocumentsPage.jsx";
import MesRdvClient from "./Component/MesRdvClient/MesRdvClient.jsx";
import Abonnement from "./Pages/billing/SubscriptionPage.jsx";
import ProfilePro from "./Pages/pro/PublicProfilePage.jsx";
import MiniCalendar from "./Pages/pro/MiniCalendar.jsx";
import CalendarPro from "./Pages/pro/calendar_pro/CalendarPro";
import AboutPage from "./Pages/marketing/AboutPage.jsx";
import CareersPage from "./Pages/marketing/CareersPage.jsx";
import HelpPage from "./Pages/marketing/HelpPage.jsx";
import PartnerRegisterPage from "./Pages/partners/PartnerRegisterPage.jsx";
import PartnerDossierPage from "./Pages/partners/PartnerDossierPage.jsx";
import EmailVerification from "./Pages/auth/EmailVerificationPage.jsx";
import ForgotPassword from "./Pages/auth/ForgotPasswordPage.jsx";
import ResetPassword from "./Pages/auth/ResetPasswordPage.jsx";

// ✅ Conditions (CGU/CGV/Confidentialité/Mentions)
import ConditionsGenerales from "./Pages/legal/TermsPage.jsx";

// ADMIN
import VerificationAnnonces from "./Pages/admin/ListingsVerificationPage.jsx";
import VerificationPros from "./Pages/admin/ProsVerificationPage.jsx";
import PartnersVerificationPage from "./Pages/admin/PartnersVerificationPage.jsx";
import MissionApplicationsAdminPage from "./Pages/admin/MissionApplicationsAdminPage.jsx";

// Choisir un profil
import ChoisirProfil from "./Pages/ChoisirProfil/ChoisirProfil.jsx";

// (optionnel) page dédiée pour re-soumettre les docs pro
import FormProResubmit from "./Component/FormProResubmit/FormProResubmit";

// 🚛 ProMeet Missions — B2B automotive marketplace
import MissionsLayout from "./Pages/missions/MissionsLayout.jsx";
import MissionsDashboard from "./Pages/missions/MissionsDashboard.jsx";
import MissionsList from "./Pages/missions/MissionsList.jsx";
import UrgentMissions from "./Pages/missions/UrgentMissions.jsx";
import MyApplications from "./Pages/missions/MyApplications.jsx";
import MyAcceptedMissions from "./Pages/missions/MyAcceptedMissions.jsx";
import CreateMission from "./Pages/missions/CreateMission.jsx";
import MissionDetails from "./Pages/missions/MissionDetails.jsx";
import MissionTracking from "./Pages/missions/MissionTracking.jsx";
import MissionPhotos from "./Pages/missions/MissionPhotos.jsx";
import MissionMessages from "./Pages/missions/MissionMessages.jsx";

/* -------------------------------
   Garde Admin
-------------------------------- */
function AdminRoute({ children }) {
  const role = localStorage.getItem("role");
  if (role !== "admin") {
    return (
      <div style={{ padding: 44, textAlign: "center", fontWeight: 600, color: "#ad4444" }}>
        ⛔ Accès réservé à l’administrateur
      </div>
    );
  }
  return children;
}

/* ---------------------------------------------------
   Garde : si pro + annonce publiée + aucun profil choisi
   => redirection vers /choisir-profil
   ⚠️ RRv6 : retourner <Outlet /> quand OK
---------------------------------------------------- */
function RequireProfilSelection() {
  const location = useLocation();

  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const selectedProfil = localStorage.getItem("selectedProfil");

  const [mustChoose, setMustChoose] = useState(false);
  const [checked, setChecked] = useState(false);

  // routes publiques (jamais bloquées)
  const isPublicRoute = useMemo(() => {
    const p = location.pathname.toLowerCase();
    return (
      p === "/" ||
      // ✅ new URLs
      p.startsWith("/auth/") ||
      p.startsWith("/about") ||
      p.startsWith("/careers") ||
      p.startsWith("/help") ||
      p.startsWith("/legal/") ||
      p.startsWith("/billing/") ||
      p.startsWith("/search") ||
      // ✅ legacy URLs (keep for compatibility)
      p.startsWith("/login") ||
      p.startsWith("/register_pro") ||
      p.startsWith("/register_user") ||
      p.startsWith("/verification") ||
      p.startsWith("/forgot_password") ||
      p.startsWith("/reset_password") ||
      p.startsWith("/apropos") ||
      p.startsWith("/admin") ||
      p.startsWith("/choisir-profil") ||
      p.startsWith("/reapply-pro") ||
      p.startsWith("/abonnement") ||
      // ✅ on whitelist la page des conditions
      p.startsWith("/conditions") ||
      // ✅ ProMeet Missions — accessible même sans profil sélectionné
      p.startsWith("/missions")
    );
  }, [location.pathname]);

  useEffect(() => {
    // routes publiques → pas de contrôle
    if (isPublicRoute) {
      setMustChoose(false);
      setChecked(true);
      return;
    }

    // pas pro connecté → pas de blocage
    if (!token || role !== "pro" || !userId) {
      setMustChoose(false);
      setChecked(true);
      return;
    }

    // profil déjà choisi → pas de blocage
    if (selectedProfil) {
      setMustChoose(false);
      setChecked(true);
      return;
    }

    // vérifier s'il existe une annonce publiée
    (async () => {
      try {
        const res = await API.get(`/api/annonces/by-user/${userId}`);
        const data = res.data ?? null;
        const annonce = Array.isArray(data) ? data[0] : data;
        const hasPublished = !!annonce && annonce.statut === "publiee";
        setMustChoose(hasPublished && !selectedProfil);
      } catch {
        setMustChoose(false);
      } finally {
        setChecked(true);
      }
    })();
  }, [role, token, userId, selectedProfil, isPublicRoute]);

  if (!checked) return null;

  if (mustChoose && location.pathname.toLowerCase() !== "/choisir-profil") {
    return <Navigate to="/choisir-profil" replace />;
  }

  return <Outlet />;
}

function LegacyVerificationRedirect() {
  const { state } = useLocation();
  return <Navigate to="/auth/verify-email" replace state={state} />;
}

function RequireMissionsAccess() {
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const token = localStorage.getItem("token");
  const [state, setState] = useState({ checked: role !== "pro", allowed: role === "admin" || role === "partner" });

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ checked: true, allowed: false });
      return undefined;
    }
    if (role === "admin" || role === "partner") {
      setState({ checked: true, allowed: true });
      return undefined;
    }
    if (role !== "pro") {
      setState({ checked: true, allowed: false });
      return undefined;
    }

    setState({ checked: false, allowed: false });
    API.get("/api/users/me")
      .then(({ data }) => {
        if (cancelled) return;
        const allowed =
          data?.proReview?.status === "approved" &&
          data?.proReview?.missionsAccess === true;
        localStorage.setItem("proCanPostMissions", allowed ? "true" : "false");
        setState({ checked: true, allowed });
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.setItem("proCanPostMissions", "false");
        setState({ checked: true, allowed: false });
      });
    return () => {
      cancelled = true;
    };
  }, [role, token]);

  if (!state.checked) return null;
  if (!token) return <Navigate to="/auth/login" replace />;
  if (!state.allowed) {
    return (
      <div style={{ padding: 44, textAlign: "center", fontWeight: 700, color: "#92400e" }}>
        Accès ProMeet Missions réservé aux partenaires et aux pros validés par l’admin avec accès missions.
      </div>
    );
  }
  return <Outlet />;
}

function PartnerOnlyRoute({ children }) {
  const role = (localStorage.getItem("role") || "").toLowerCase();
  if (role !== "partner" && role !== "admin") {
    return <Navigate to="/" replace />;
  }
  return children;
}

/* -------------------------------
   Google Maps : un seul chargement global (voir utils/loadGoogleMapsScript)
-------------------------------- */
function useGoogleMapsLoader() {
  useEffect(() => {
    loadGoogleMapsPlaces().catch(() => {});
  }, []);
}

/* -------------------------------
   App
-------------------------------- */
function App() {
  useGoogleMapsLoader();

  return (
    <RefreshProvider>
      <Router>
        <Header />
        <NativeNotificationBridge />
        <CookieConsent />

        <Routes>
          {/* --------- Public --------- */}
          <Route path="/" element={<Home />} />
          {/* ✅ New clean URLs */}
          <Route path="/search" element={<SearchProsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/legal/terms" element={<ConditionsGenerales />} />
          <Route path="/billing/subscription" element={<Abonnement />} />

          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/signup/pro" element={<RegisterPro />} />
          <Route path="/auth/signup/partner" element={<PartnerRegisterPage />} />
          <Route path="/auth/signup/client" element={<RegisterUser />} />
          <Route path="/auth/verify-email" element={<EmailVerification />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password/:token" element={<ResetPassword />} />

          {/* ✅ Legacy URLs (aliases) */}
          <Route path="/login" element={<Navigate to="/auth/login" replace />} />
          <Route path="/register_pro" element={<Navigate to="/auth/signup/pro" replace />} />
          <Route path="/register_user" element={<Navigate to="/auth/signup/client" replace />} />
          <Route path="/verification" element={<LegacyVerificationRedirect />} />
          <Route path="/forgot_password" element={<Navigate to="/auth/forgot-password" replace />} />
          <Route path="/reset_password/:token" element={<ResetPassword />} />
          <Route path="/apropos" element={<Navigate to="/about" replace />} />
          <Route path="/conditions" element={<Navigate to="/legal/terms" replace />} />
          <Route path="/abonnement" element={<Navigate to="/billing/subscription" replace />} />

          {/* ✅ Pro setup / misc (keep public) */}
          <Route path="/pro/profiles/select" element={<ChoisirProfil />} />
          <Route path="/choisir-profil" element={<Navigate to="/pro/profiles/select" replace />} />
          <Route path="/reapply-pro" element={<FormProResubmit />} />

          {/* --------- Protégé par la garde de sélection de profil --------- */}
          <Route element={<RequireProfilSelection />}>
            {/* ✅ New pro URLs */}
            <Route path="/pro/dashboard" element={<HomePro />} />
            <Route path="/pro/listing" element={<AnnonceForm />} />
            <Route path="/pro/listing/edit/:id" element={<EditAnnonce />} />
            <Route path="/pro/listing/edit" element={<EditAnnonce />} />
            <Route path="/pro/calendar" element={<CalendarPro />} />
            <Route path="/pro/appointments" element={<MesRdv />} />
            <Route path="/pro/settings" element={<Settings />} />
            <Route path="/pro/documents/:id" element={<MesDocuments />} />
            <Route path="/client/appointments" element={<MesRdvClient />} />
            <Route path="/pro/public-profile" element={<ProfilePro />} />
            <Route path="/pro/mini-calendar" element={<MiniCalendar />} />

            {/* ✅ Legacy protected URLs (aliases) */}
            <Route path="/home_pro" element={<Navigate to="/pro/dashboard" replace />} />
            <Route path="/annonce_pro" element={<Navigate to="/pro/listing" replace />} />
            <Route path="/editannonce/:id" element={<Navigate to="/pro/listing/edit/:id" replace />} />
            <Route path="/editannonce" element={<Navigate to="/pro/listing/edit" replace />} />
            <Route path="/calendar_pro" element={<Navigate to="/pro/calendar" replace />} />
            <Route path="/mesrdv_pro" element={<Navigate to="/pro/appointments" replace />} />
            <Route path="/settings_pro/:id" element={<Navigate to="/pro/settings" replace />} />
            <Route path="/mesdocuments/:id" element={<Navigate to="/pro/documents/:id" replace />} />
            <Route path="/mesrdv_client" element={<Navigate to="/client/appointments" replace />} />
            <Route path="/profilepro" element={<Navigate to="/pro/public-profile" replace />} />
            <Route path="/minicalendar" element={<Navigate to="/pro/mini-calendar" replace />} />

            {/* (à refactor) pages legacy internes */}
            <Route path="/welcome_pro/:id" element={<WelcomePro />} />
            <Route path="/welcome_user/:id" element={<Navigate to="/search" replace />} />
            <Route path="/welcome_user" element={<Navigate to="/search" replace />} />
            <Route path="/activityCard" element={<ActivityCard />} />

            <Route
              path="/partner/mon-dossier"
              element={
                <PartnerOnlyRoute>
                  <PartnerDossierPage />
                </PartnerOnlyRoute>
              }
            />

            <Route element={<RequireMissionsAccess />}>
              <Route path="/missions" element={<MissionsLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<MissionsDashboard />} />
                <Route path="list" element={<MissionsList />} />
                <Route path="urgent" element={<UrgentMissions />} />
                <Route path="applications" element={<MyApplications />} />
                <Route path="accepted" element={<MyAcceptedMissions />} />
                <Route path="create" element={<CreateMission />} />
                <Route path="tracking" element={<MissionTracking />} />
                <Route path="tracking/:id" element={<MissionTracking />} />
                <Route path="photos" element={<MissionPhotos />} />
                <Route path=":id/photos" element={<MissionPhotos />} />
                <Route path="messages" element={<MissionMessages />} />
                <Route path=":id/messages" element={<MissionMessages />} />
                <Route path=":id" element={<MissionDetails />} />
              </Route>
            </Route>

            {/* ----- ADMIN ----- */}
            <Route
              path="/admin/listings"
              element={
                <AdminRoute>
                  <VerificationAnnonces />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/pros"
              element={
                <AdminRoute>
                  <VerificationPros />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/partners"
              element={
                <AdminRoute>
                  <PartnersVerificationPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/missions"
              element={
                <AdminRoute>
                  <MissionApplicationsAdminPage />
                </AdminRoute>
              }
            />

            {/* Legacy admin URLs */}
            <Route
              path="/admin/verification-annonces"
              element={<Navigate to="/admin/listings" replace />}
            />
            <Route
              path="/admin/verification-pros"
              element={<Navigate to="/admin/pros" replace />}
            />
          </Route>

          {/* 404 → Accueil */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <AppFooter />
      </Router>
    </RefreshProvider>
  );
}

export default App;
